import { Stack } from '@global-ecom/nitro-uds/elements';
import React, { useEffect } from 'react';

import { Link as LegacyLink } from 'ui/elements/Link';
import { buildProductUrl } from 'utils/buildProductUrl';
import { getSizesFromSizeGroups } from 'utils/products';
import { Product, Size, Variant } from '__generated__/graphql';
import { useModalsOpenStore } from 'store';
import { usePageEventsContext } from 'hooks/usePageEventsContext';
import { event, AnalyticsEvents } from 'utils/analytics';

import { ProductListItemImages } from '../../ProductListItemImages';
import { Sizes } from '../../Sizes';

import { GWPNew_ColorPicker } from './GWP.new.ColorPicker';

export const GWPNew_Product = ({
  selectedProduct,
  selectedVariant,
  setSelectedVariant,
  selectedSize,
  setSelectedSize,
}: {
  selectedProduct?: Product;
  selectedVariant?: Variant;
  setSelectedVariant: React.Dispatch<React.SetStateAction<Variant | undefined>>;
  selectedSize?: Size;
  setSelectedSize: React.Dispatch<React.SetStateAction<Size | undefined>>;
}) => {
  const { setModalOpen } = useModalsOpenStore();

  const variations = selectedProduct?.variations;
  const { pageviewEventHasFired } = usePageEventsContext();

  useEffect(() => {
    // when the product (variations) is changed
    // -> set the first variation as the selected one
    // -> reset the selected size to undefined

    setSelectedVariant(variations?.[0]);
    setSelectedSize(undefined);
  }, [setSelectedSize, setSelectedVariant, variations]);

  if (!selectedProduct || !selectedVariant) return null;

  const triggerEventChooseSize = ({ productName, variantId }) => {
    if (pageviewEventHasFired) {
      event(AnalyticsEvents.GA4_CustomEvent, {
        event_name: AnalyticsEvents.CHOOSE_SIZE,
        event_params: {
          item_id_ep: variantId,
          item_name_ep: productName,
          is_gift: 'true',
        },
      });
    }
  };
  return (
    <Stack gap="sm">
      <ProductListItemImages variant={selectedVariant} />
      <LegacyLink
        href={buildProductUrl(selectedProduct.id, selectedProduct.name, {
          swatch: selectedVariant.colorValue,
        })}
        onClick={() => setModalOpen('GWPModal', false)}
        className="hover:underline hover:cursor-pointer font-bold text-xl"
      >
        {selectedProduct.name}
      </LegacyLink>
      {variations && (
        <GWPNew_ColorPicker
          variations={variations}
          selectedVariant={selectedVariant}
          setSelectedVariant={setSelectedVariant}
          productName={selectedProduct.name}
          productId={selectedProduct.id}
        />
      )}
      <div className="h-0.5 bg-gray-300 opacity-50 my-2" />
      <Sizes
        sizeGroups={selectedVariant.sizeGroups}
        desiredSize={selectedSize?.id}
        onChange={sizeId => {
          const desiredSize = getDesiredSize({
            variant: selectedVariant,
            sizeId,
          });
          setSelectedSize(desiredSize);
          triggerEventChooseSize({
            productName: selectedProduct.name,
            variantId: selectedVariant.id,
          });
        }}
      />
    </Stack>
  );
};

// ──── UTILS FOR ONLY THIS FILE ─────────────────────────────────────────────────────────────────────────

const getDesiredSize = ({
  variant,
  sizeId,
}: {
  variant: Variant;
  sizeId: string;
}) => {
  const sizes = getSizesFromSizeGroups(variant.sizeGroups);
  const desiredSize = sizes.find(size => size.id === sizeId);

  return desiredSize;
};
